import {
  MODOOCAR_BASE_URL,
  MODOOCAR_RANK_ROUTE,
  MYCLE_HOME_URL,
} from '@/app/(makers)/modoocar/_constants'
import { IS_PRODUCTION } from '@/shared/lib'

import type { TFuelType } from '../_types'

export { MOCK_MODOOCAR } from './mockModoocar'
export { MODOOCAR_BASE_URL, MYCLE_HOME_URL }

/** [정책]: 유류비는 GEO 인용이 흔들리지 않도록 실시간 유가 대신 분기 고정 유가로 계산합니다. */
export const ANNUAL_DISTANCE_KM = 20_000

export const MONTHS_PER_YEAR = 12

export const MAN_WON = 10_000

/** [정책]: 모바일은 검색 유입이면 SmartScript 동적 OneLink(`?seoOnelink=`)를 먼저 쓰고, 이 값은 PC와 폴백에 씁니다. */
export const MODOOCAR_CTA_ONELINK_URL = IS_PRODUCTION
  ? 'https://web-seo-geo.onelink.me/kFy7/5a41jlio'
  : 'https://web-dev-seo-geo.onelink.me/hCvn/fhzhwhbm'
export const MODOOCAR_PROMOTION_BANNER_ONELINK_URL =
  'https://web-seo-geo.onelink.me/kFy7/ycs7bt49'
export const MODOOCAR_REVIEW_APP_ONELINK_URL = IS_PRODUCTION
  ? 'https://web-seo-geo.onelink.me/kFy7/0g7rblze'
  : 'https://web-dev-seo-geo.onelink.me/hCvn/cn6pxp1b'
export const MODOOCAR_MILEAGE_LOCK_ONELINK_URL = IS_PRODUCTION
  ? 'https://web-seo-geo.onelink.me/kFy7/hgev4ya3'
  : 'https://web-dev-seo-geo.onelink.me/hCvn/pgjqjqlr'
export const MODOOCAR_TREND_LOCK_ONELINK_URL = IS_PRODUCTION
  ? 'https://web-seo-geo.onelink.me/kFy7/vgff01lt'
  : 'https://web-dev-seo-geo.onelink.me/hCvn/euoix1db'

/** [주의]: 이벤트 label에도 같은 값을 씁니다. 문구를 바꾸면 이벤트 label도 바뀝니다. */
export const APP_CTA_BUTTON_TEXT = {
  DEFAULT: '마이클 앱에서 후기 더보기',
  LOCK: '앱에서 자세히 보기',
} as const

export const getAppCtaButtonText = (isLock: boolean): string =>
  isLock ? APP_CTA_BUTTON_TEXT.LOCK : APP_CTA_BUTTON_TEXT.DEFAULT

export const MODOOCAR_EVENT = {
  PAGE_SHOW: 'common_web_modoocar_page_show',
  SECTION_SHOW: 'common_web_modoocar_section_show',
  ITEM_SHOW: 'common_web_modoocar_item_show',
  ITEM_CLICK: 'common_web_modoocar_item_click',
  ITEM_CHANGE: 'common_web_modoocar_item_change',
  LOGO_CLICK: 'common_web_modoocar_logo_click',
} as const

export const MODOOCAR_CODE = {
  PAGE: 'page',
  HEADER: 'header',
  HERO: 'hero',
  MODEL_INFO: 'model_info',
  PROMOTION_BANNER: 'promotion_banner',
  BOTTOM_CTA: 'bottom_cta',
  SIDE_CTA: 'side_cta',
  TAB_NAV: 'tab_nav',
  REAL_MILEAGE: 'real_mileage',
  FUEL_COST: 'fuel_cost',
  OWNER_REVIEW: 'owner_review',
  MODEL_COMPARE: 'model_compare',
  REPAIR_PROMOTION: 'repair_promotion',
  SERVICE_REVIEW: 'service_review',
  SHARE: 'share',
  FOOTER: 'footer',
  SATISFACTION_RATING: 'satisfaction_rating',
  RATING_COMPLETE: 'rating_complete',
  REVIEW_PROMPT: 'review_prompt',
  AB_EXPOSURE: 'ab_exposure',
  /** [주의]: 잠금 화면 code는 OneLink af_sub2와 같은 값이어야 합니다. 다르면 앱 설치를 이벤트와 연결할 수 없습니다. */
  LOCK_MILEAGE: 'lock_mileage',
  LOCK_TREND: 'lock_trend',
  LOCK_REVIEW: 'lock_review',
} as const

export const MODOOCAR_CAR_FALLBACK_IMAGE_URL =
  'https://mycle.co.kr/resources/images/car/img_seo_car_default.png'
export const MODOOCAR_BIKE_FALLBACK_IMAGE_URL =
  'https://mycle.co.kr/resources/images/car/img_seo_bike_default.png'

export type TModoocarRankTab = 'score' | 'efficiency' | 'fuelCost'

/** [정책]: 하이브리드는 랭킹 유종이 따로 없어서 휘발유로 합칩니다. */
const FUEL_DOMAIN_TO_RANK_FUEL: Record<TFuelType, string> = {
  휘발유: '휘발유',
  디젤: '디젤',
  LPG: 'LPG',
  하이브리드: '휘발유',
  전기: '전기',
}

interface IBuildModoocarRankPathParams {
  tab: TModoocarRankTab
  segment: string
  fuelType: TFuelType
}

export const buildModoocarRankPath = ({
  tab,
  segment,
  fuelType,
}: IBuildModoocarRankPathParams): string =>
  `${MODOOCAR_RANK_ROUTE}/${tab}` +
  `/${encodeURIComponent(segment)}` +
  `/${encodeURIComponent(FUEL_DOMAIN_TO_RANK_FUEL[fuelType])}`

export const REVIEW_LABEL_MAX_LENGTH = 40

export const OWNER_RATING_DEFAULT_SCORE = 5
export const OWNER_RATING_LABEL: Record<number, string> = {
  1: '많이 아쉬워요',
  2: '별로예요',
  3: '보통이에요',
  4: '만족해요',
  5: '최고예요',
}
/** [정책]: 별점 팝업 제목의 세부모델명이 이 길이보다 길면 '내 차'로 대신합니다. */
export const OWNER_RATING_MODEL_NAME_MAX_LENGTH = 15
export const OWNER_REVIEW_WRITE_DEEPLINK =
  'macarongapp:///modoo/carreview/write'
/** [앱]: 뒤에 /{차종명}/{제조사}를 붙여서 씁니다. */
export const OWNER_REVIEW_LIST_DEEPLINK = 'macarongapp:///modoo/carreview/list'
export const MACAR_SERVER_ID_PARAM = 'macarServerId'

const MOTORCYCLE_VEHICLE_TYPES = ['모터사이클', '오토바이', 'motorcycle']

export const isMotorcycleVehicleType = (value?: string): boolean =>
  Boolean(value && MOTORCYCLE_VEHICLE_TYPES.includes(value.toLowerCase()))
